import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MemberNote } from 'src/app/models/note';
import { ConfigurationService, ForceBaseUrl } from '../configuration/configuration.service';
import { EndpointFactory } from '../endpoint-factory.service';


@Injectable({
    providedIn: 'root'
})
export class MemberNoteEndpoint extends EndpointFactory {

    getServiceBaseUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('note', force) + 'note';
    }

    constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
        super(http, configurations, injector);
    }

    getMemberNotes(tenantId: string, memberId: string): Observable<MemberNote[]> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}`;

        return this.http.get<MemberNote[]>(endpointUrl)
            .pipe(map(noteResponse => noteResponse.map(memberNoteItem => new MemberNote(memberNoteItem))))
            .pipe(
                catchError(error => {
                    return this.handleError(error);
                }));
    }

    getNotesForMember(tenantId: string, memberId: string): Promise<MemberNote[]> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}`;
        return lastValueFrom(this.http.get<MemberNote[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })));
    }

    getFacilityListMemberNotes(tenantId: string, memberId: string, facilityListNotesId: string) {
        const endpointURl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/${facilityListNotesId}`;

        return this.http.get<MemberNote[]>(endpointURl)
            .pipe(map(noteResponse => noteResponse.map(facilityListNoteItem => new MemberNote(facilityListNoteItem))))
            .pipe(
                catchError(error => {
                    return this.handleError(error);
                }));
    }

    addOrUpdate(tenantId: string, note: MemberNote): Observable<MemberNote> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}`;

        return this.http.put<MemberNote>(endpointUrl, note)
            .pipe(map(noteResponse => new MemberNote(noteResponse)))
            .pipe<MemberNote>(
                catchError(error => {
                    return this.handleError(error);
                }));
    }

    updateNote(tenantId: string, note: MemberNote): Promise<MemberNote> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}`;
        return lastValueFrom(this.http.put<MemberNote>(endpointUrl, note).pipe(
            catchError(error => {
                return this.handleError(error);
            })));             
    }

    addOrUpdateFacilityListNotes(facilityId: string, note: MemberNote): Observable<MemberNote> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${facilityId}`;


        return this.http.put<MemberNote>(endpointUrl, note)
            .pipe(map(noteResponse => new MemberNote(noteResponse)))
            .pipe<MemberNote>(
                catchError(error => {
                    return this.handleError(error);
                }));
    }


    setAsPrimary(tenantId: string, memberId: string, noteId: number): Observable<MemberNote[]> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/updateprimary/${memberId}/${noteId}`;

        return this.http.put<MemberNote[]>(endpointUrl, null)
            .pipe<MemberNote[]>(map(notesResponse => notesResponse.map(noteResponse => new MemberNote(noteResponse))))
            .pipe(
                catchError(error => {
                    return this.handleError(error);
                }));
    }

    delete(tenantId: string, noteId: number): Observable<boolean> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${noteId}`;

        return this.http.delete<boolean>(endpointUrl)
            .pipe(
                catchError(error => {
                    return this.handleError(error);
                }));
    } 

    deleteNote(tenantId: string, noteId: number): Promise<boolean> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${noteId}`;

        return lastValueFrom(this.http.delete<boolean>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })));
    }

}
